import { useCallback, useState, FC } from 'react'
import Dropzone, { Accept } from 'react-dropzone'

import { Alert } from 'ui'
import styles from './fileUpload.module.scss'
import { MAX_FILE_SIZE } from 'constant'

import { t } from 'translations'

type Props = {
  validExtensions?: string[]
  disabled?: boolean
  accept?: Accept
  onChange: (file?: File) => Promise<void> | void
  maxSize?: number
}
const FileUpload: FC<Props> = ({
  validExtensions = [],
  disabled,
  onChange,
  accept = { 'image/*': [], 'application/pdf': ['.pdf'] },
  maxSize = MAX_FILE_SIZE
}) => {
  const [wrongType, setWrongType] = useState(false)
  const [isLarge, setIsLarge] = useState(false)

  if ('text/csv' in accept)
    console.error(
      "Windows doesn't always give the correct file type for csv files, please use validExtensions=['csv']. More reading here: https://christianwood.net/posts/csv-file-upload-validation/"
    )

  const onDrop = useCallback(
    (files, errors) => {
      if (errors.length) {
        if (errors[0].errors.some(error => error.code === 'file-too-large')) setIsLarge(true)
        if (errors[0].errors.some(error => error.code === 'file-invalid-type')) setWrongType(true)
      } else {
        setIsLarge(false)
        setWrongType(false)

        const file = files[0]

        if (!file) return onChange()

        if (!validExtensions.length) onChange(file)
        else {
          if (validExtensions.includes(file.name.split('.').pop())) return onChange(file)
          setWrongType(true)
          onChange()
        }
      }
    },
    [onChange, validExtensions]
  )
  return (
    <>
      <Alert variant="danger" show={!!wrongType}>
        {t('forms.fileUpload.INCORRECT_FILE_TYPE')}
      </Alert>
      <Alert variant="danger" show={!!isLarge}>
        {t('forms.fileUpload.TOO_LARGE')}
      </Alert>
      {/* https://github.com/react-dropzone/react-dropzone/issues/276 */}
      <Dropzone onDrop={onDrop} disabled={disabled} accept={accept} maxSize={maxSize}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <section>
            <div {...getRootProps()} className={`${styles.dropzone} ${isDragActive ? styles['is-over'] : null}`}>
              <input {...getInputProps()} id="file" data-cy="dropZone" data-testid="dropzone" />
              <p>{t('forms.fileUpload.DRAG_AND_DROP_FILE')}</p>
            </div>
          </section>
        )}
      </Dropzone>
    </>
  )
}

export { FileUpload }
